import { APP_SCHEME } from '@circa/shared/config';
import { useSyncExternalStore } from 'react';
import { back, navigate } from './router';

// Where we're running: the web/PWA build or the Android app (Capacitor). Native-only
// features are loaded lazily so the web bundle stays small.

export const isCapacitor =
  typeof window !== 'undefined' &&
  !!(
    window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }
  ).Capacitor?.isNativePlatform?.();
export const isAndroid = isCapacitor && /Android/i.test(navigator.userAgent);
export const isNativeApp = isCapacitor;
/** Running from files bundled into the app (Android), so the user has to say which server to use. */
export const isBundled = isCapacitor;
export const isStandalone =
  isNativeApp ||
  (typeof matchMedia !== 'undefined' && matchMedia('(display-mode: standalone)').matches);
export const isTouch = typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches;
export const isMac = /Mac/.test(navigator.platform);

const MOBILE_QUERY = '(max-width: 820px)';
const mq = typeof matchMedia !== 'undefined' ? matchMedia(MOBILE_QUERY) : null;

/** Phone-sized layout: single pane, bottom nav, drawer sidebar, big touch targets. */
export function useIsMobile(): boolean {
  return useSyncExternalStore(
    (cb) => {
      mq?.addEventListener('change', cb);
      return () => mq?.removeEventListener('change', cb);
    },
    () => mq?.matches ?? false,
  );
}

export const isMobileNow = () => mq?.matches ?? false;

export async function haptic(kind: 'light' | 'medium' | 'success' = 'light') {
  if (!isCapacitor) {
    if (isTouch) navigator.vibrate?.(kind === 'light' ? 8 : 15);
    return;
  }
  try {
    const { Haptics, ImpactStyle, NotificationType } = await import('@capacitor/haptics');
    if (kind === 'success') await Haptics.notification({ type: NotificationType.Success });
    else
      await Haptics.impact({ style: kind === 'medium' ? ImpactStyle.Medium : ImpactStyle.Light });
  } catch {}
}

/** Turns "circa://open/sleep" (launcher icons, shortcuts, notifications) into a route. */
export function routeFromDeepLink(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.protocol === `${APP_SCHEME}:`) {
      const path = `/${u.host === 'open' ? '' : `${u.host}/`}${u.pathname.replace(/^\/+/, '')}`;
      return (path.replace(/\/+$/, '') || '/today') + u.search;
    }
    if (u.origin === location.origin) return u.pathname + u.search;
  } catch {}
  return null;
}

type BackHandler = () => boolean;
const backHandlers: BackHandler[] = [];
/** Sheets and drawers register here so Android's back button closes them first. */
export function pushBackHandler(h: BackHandler): () => void {
  backHandlers.push(h);
  return () => {
    const i = backHandlers.lastIndexOf(h);
    if (i >= 0) backHandlers.splice(i, 1);
  };
}

export function handleBack(): boolean {
  for (let i = backHandlers.length - 1; i >= 0; i--) {
    if (backHandlers[i]!()) return true;
  }
  return back();
}

/** Native hooks: Android back button, deep links, resume. */
export async function initPlatform(onResume: () => void) {
  if (isCapacitor) {
    const { App } = await import('@capacitor/app');
    App.addListener('backButton', () => {
      if (!handleBack()) void App.minimizeApp();
    });
    App.addListener('appUrlOpen', ({ url }) => {
      const r = routeFromDeepLink(url);
      if (r) navigate(r);
    });
    App.addListener('resume', onResume);
    const launch = await App.getLaunchUrl();
    if (launch?.url) {
      const r = routeFromDeepLink(launch.url);
      if (r) navigate(r, { replace: true });
    }
    try {
      const { SplashScreen } = await import('@capacitor/splash-screen');
      await SplashScreen.hide();
    } catch {}
  }
}

/** Opens a web link outside the app (a new tab; the system browser on Android). */
export async function openExternal(url: string): Promise<void> {
  window.open(url, '_blank', 'noopener');
}
